"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { track } from "@vllnt/analytics";
import {
  ShareDialog,
  type ShareDialogPlatform,
  SocialFAB,
  type SocialFabActionConfig,
} from "@vllnt/ui";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

import {
  FAB_BASE_OFFSET,
  FAB_ELEMENT_MARGIN,
  getBaseFabOffset,
} from "@/lib/fab-config";
import {
  buildBlueskyShareUrl,
  buildCopyUrl,
  buildFacebookShareUrl,
  buildLinkedInShareUrl,
  buildXShareUrl,
  SOCIAL_URLS,
} from "@/lib/social";

const sharePlatforms: ShareDialogPlatform[] = [
  { buildUrl: buildXShareUrl, key: "x", label: "X (Twitter)" },
  { buildUrl: buildLinkedInShareUrl, key: "linkedin", label: "LinkedIn" },
  { buildUrl: buildFacebookShareUrl, key: "facebook", label: "Facebook" },
  { buildUrl: buildBlueskyShareUrl, key: "bluesky", label: "Bluesky" },
];

/**
 * Check if pathname is an app route (e.g. /en/apps/image-editor)
 * Pattern: /{locale}/apps/{slug}
 */
function isAppRoute(path: string): boolean {
  return /^\/[a-z]{2}\/apps\/[^/]+/.test(path);
}

// Right offset matches header spacing (gap-4 = 16px)
const DEFAULT_RIGHT_OFFSET = FAB_BASE_OFFSET;

/**
 * Hook to calculate extra bottom offset when footer would overlap with FAB
 * Adds offset when necessary to prevent overlap, not based on full footer visibility
 */
function useFooterOffset(baseOffset: number) {
  const [footerOffset, setFooterOffset] = useState(0);

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;
    if (typeof IntersectionObserver === "undefined") return;

    // IntersectionObserver-driven: avoids per-scroll getBoundingClientRect
    // (which causes forced reflows on every scroll event).
    const computeNextOffset = (entry: IntersectionObserverEntry): number => {
      if (!entry.isIntersecting) return 0;
      const footerTopFromBottom =
        window.innerHeight - entry.intersectionRect.top;
      return footerTopFromBottom > baseOffset
        ? footerTopFromBottom - baseOffset + FAB_ELEMENT_MARGIN
        : 0;
    };
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        setFooterOffset(computeNextOffset(entry));
      },
      {
        // Root margin shifts the observer trigger up from viewport bottom by
        // the FAB area height, so we fire BEFORE visual overlap.
        rootMargin: `0px 0px -${baseOffset + FAB_ELEMENT_MARGIN}px 0px`,
        threshold: [0, 0.01, 0.5, 1],
      },
    );

    observer.observe(footer);
    return () => {
      observer.disconnect();
    };
  }, [baseOffset]);

  return footerOffset;
}

// eslint-disable-next-line max-lines-per-function -- component with extensive render logic
export function FloatingSocialFAB() {
  const t = useTranslations("fab");
  const locale = useLocale();
  const pathname = usePathname();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const hidden = pathname ? isAppRoute(pathname) : false;

  // Get base offset (page-specific) then calculate footer offset
  const baseOffset = getBaseFabOffset(pathname ?? "");
  const footerOffset = useFooterOffset(baseOffset);
  const bottomOffset = baseOffset + footerOffset;

  const handleOpen = useCallback(
    (trigger: string, device: string) => {
      track("fab_open", { device, locale, page_path: pathname, trigger });
    },
    [locale, pathname],
  );

  const handleClose = useCallback(
    (trigger: string) => {
      track("fab_close", { locale, page_path: pathname, trigger });
    },
    [locale, pathname],
  );

  const handleAction = useCallback(
    (actionId: string) => {
      track("fab_action", { action: actionId, locale, page_path: pathname });
    },
    [locale, pathname],
  );

  const handleShareDialogOpen = useCallback(() => {
    handleAction("share_dialog_open");
    setShareDialogOpen(true);
  }, [handleAction]);

  const handleSharePlatformSelect = useCallback(
    (platformKey: string) => {
      track("share_platform_select", {
        locale,
        page_path: pathname,
        platform: platformKey,
      });
    },
    [locale, pathname],
  );

  const handleCopyLink = useCallback(() => {
    track("share_copy_link", { locale, page_path: pathname });
  }, [locale, pathname]);

  const actions: SocialFabActionConfig[] = useMemo(
    () => [
      {
        id: "share",
        label: t("share"),
        onClick: handleShareDialogOpen,
      },
      {
        id: "x_follow",
        label: t("follow_x"),
        onClick: () => {
          handleAction("x_follow");
          window.open(SOCIAL_URLS.x, "_blank", "noopener,noreferrer");
        },
      },
      {
        id: "discord_join",
        label: t("join_discord"),
        onClick: () => {
          handleAction("discord_join");
          window.open(SOCIAL_URLS.discord, "_blank", "noopener,noreferrer");
        },
      },
    ],
    [t, handleAction, handleShareDialogOpen],
  );

  // Hide FAB on full-screen app routes
  if (hidden) return null;

  return (
    <>
      <SocialFAB
        actions={actions}
        bottomOffset={bottomOffset}
        labels={{ close: t("close"), share: t("share") }}
        onAction={handleAction}
        onClose={handleClose}
        onOpen={handleOpen}
        rightOffset={DEFAULT_RIGHT_OFFSET}
      />
      <ShareDialog
        buildCopyUrl={buildCopyUrl}
        description={t("share_description")}
        labels={{ copied: t("copied"), copyLink: t("copy_link") }}
        onCopy={handleCopyLink}
        onOpenChange={setShareDialogOpen}
        onShare={handleSharePlatformSelect}
        open={shareDialogOpen}
        platforms={sharePlatforms}
        title={t("share_title")}
      />
    </>
  );
}
